import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Bell,
  CurrencyInr,
  Clock,
  Receipt,
  EnvelopeSimple,
  WarningCircle,
} from "@phosphor-icons/react";
import { toast } from "sonner";

function fmt(n) {
  return Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });
}

function daysSince(dateStr) {
  if (!dateStr) return 0;
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return 0;
  const today = new Date();
  return Math.max(0, Math.floor((today - d) / (1000 * 60 * 60 * 24)));
}

function ageBadgeClass(days) {
  if (days >= 60) return "bg-red-100 text-red-700 border-red-200";
  if (days >= 30) return "bg-rose-50 text-rose-700 border-rose-200";
  if (days >= 14) return "bg-amber-50 text-amber-700 border-amber-200";
  if (days >= 7) return "bg-yellow-50 text-yellow-700 border-yellow-200";
  return "bg-slate-50 text-slate-600 border-slate-200";
}

// Mirror of backend cadence tiers — keep in sync with routes/invoices.py
const CADENCE_TIERS = [
  { min_days: 60, level: "final", label: "Final notice", className: "bg-red-600 text-white border-red-700" },
  { min_days: 30, level: "escalated", label: "Escalated", className: "bg-rose-100 text-rose-800 border-rose-300" },
  { min_days: 14, level: "firm", label: "Firm reminder", className: "bg-amber-100 text-amber-800 border-amber-300" },
  { min_days: 0, level: "gentle", label: "Gentle reminder", className: "bg-slate-100 text-slate-700 border-slate-300" },
];

function cadenceFor(days) {
  return CADENCE_TIERS.find((t) => days >= t.min_days) || CADENCE_TIERS[CADENCE_TIERS.length - 1];
}

export default function ARFollowUps() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [recent, setRecent] = useState({});
  const [selected, setSelected] = useState({}); // {invoice_id: true}
  const [minOverdue, setMinOverdue] = useState(0);
  const [sending, setSending] = useState(false);
  const [rowStatus, setRowStatus] = useState({}); // per-row send state during bulk

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [inv, rem] = await Promise.all([
        api.get("/invoices", { params: { status: "issued" } }),
        api.get("/invoices/reminders/recent").catch(() => ({ data: {} })),
      ]);
      const unpaid = (inv.data || []).filter((r) => !r.paid);
      // Oldest first
      unpaid.sort((a, b) => (a.date || "").localeCompare(b.date || ""));
      setRows(unpaid);
      setRecent(rem.data || {});
    } catch (err) {
      console.error("Failed to load AR data:", err);
      toast.error("Failed to load follow-ups");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    return rows.filter((r) => daysSince(r.date) >= Number(minOverdue || 0));
  }, [rows, minOverdue]);

  const eligible = useMemo(() => {
    return filtered.filter(
      (r) => !recent[r.id] && (r.customer?.email || "").trim() !== "",
    );
  }, [filtered, recent]);

  const totals = useMemo(() => {
    const total = filtered.reduce(
      (s, r) => s + Number(r.calculation?.total || 0),
      0,
    );
    const oldest = filtered[0] ? daysSince(filtered[0].date) : 0;
    return { total, count: filtered.length, oldest };
  }, [filtered]);

  const selectedIds = useMemo(
    () => Object.keys(selected).filter((id) => selected[id]),
    [selected],
  );

  const selectedEligibleIds = useMemo(
    () =>
      selectedIds.filter((id) => {
        const row = rows.find((r) => r.id === id);
        return row && !recent[id] && (row.customer?.email || "").trim() !== "";
      }),
    [selectedIds, rows, recent],
  );

  const toggleAll = (checked) => {
    if (!checked) {
      setSelected({});
      return;
    }
    const next = {};
    eligible.forEach((r) => {
      next[r.id] = true;
    });
    setSelected(next);
  };

  const allEligibleSelected =
    eligible.length > 0 && eligible.every((r) => selected[r.id]);

  const sendBulk = async () => {
    if (selectedEligibleIds.length === 0) {
      toast.error("Nothing eligible to remind");
      return;
    }
    setSending(true);
    let okCount = 0;
    let failCount = 0;
    const updates = {};
    for (const id of selectedEligibleIds) {
      setRowStatus((p) => ({ ...p, [id]: "sending" }));
      try {
        const { data } = await api.post(`/invoices/${id}/reminder`, {});
        okCount += 1;
        updates[id] = {
          sent_at: new Date().toISOString(),
          days_ago: 0,
          email_id: data.email_id,
          cooldown_days_remaining: 7,
        };
        setRowStatus((p) => ({ ...p, [id]: "sent" }));
      } catch (err) {
        failCount += 1;
        setRowStatus((p) => ({
          ...p,
          [id]: err?.response?.data?.detail || "failed",
        }));
      }
    }
    setRecent((p) => ({ ...p, ...updates }));
    setSelected({});
    setSending(false);
    if (okCount > 0 && failCount === 0) {
      toast.success(`Reminder${okCount === 1 ? "" : "s"} sent to ${okCount} customer${okCount === 1 ? "" : "s"}`);
    } else if (okCount > 0) {
      toast.warning(`Sent ${okCount}; ${failCount} failed`, {
        description: "See per-row status badges for details.",
        duration: 8000,
      });
    } else {
      toast.error(`All ${failCount} reminder${failCount === 1 ? "" : "s"} failed`);
    }
  };

  return (
    <div data-testid="ar-followups-page">
      <div className="glass-header sticky top-0 z-10 px-8 py-5">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">
              Receivables
            </div>
            <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">
              AR Follow-ups
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Unpaid invoices, oldest first. Bulk select and send reminders in one go.
            </p>
          </div>
          <button
            onClick={() => navigate("/invoices")}
            className="inline-flex items-center gap-2 px-3 h-10 rounded-md border border-slate-200 bg-white text-slate-700 text-sm font-medium hover:bg-slate-50"
            data-testid="back-to-invoices"
          >
            All invoices
          </button>
        </div>
      </div>

      <div className="p-8 space-y-5">
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="bg-white border border-slate-200 rounded-lg p-6">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">
                  Outstanding
                </div>
                <div className="mt-2 font-heading text-2xl font-bold text-slate-900">
                  ₹{fmt(totals.total)}
                </div>
              </div>
              <div className="w-10 h-10 rounded-md bg-rose-50 text-rose-700 flex items-center justify-center">
                <CurrencyInr size={20} weight="duotone" />
              </div>
            </div>
          </div>
          <div className="bg-white border border-slate-200 rounded-lg p-6">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">
                  Unpaid invoices
                </div>
                <div className="mt-2 font-heading text-2xl font-bold text-slate-900">
                  {totals.count}
                </div>
              </div>
              <div className="w-10 h-10 rounded-md bg-amber-50 text-amber-700 flex items-center justify-center">
                <Receipt size={20} weight="duotone" />
              </div>
            </div>
          </div>
          <div className="bg-white border border-slate-200 rounded-lg p-6">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">
                  Oldest age
                </div>
                <div className="mt-2 font-heading text-2xl font-bold text-slate-900">
                  {totals.oldest}d
                </div>
              </div>
              <div className="w-10 h-10 rounded-md bg-slate-50 text-slate-700 flex items-center justify-center">
                <Clock size={20} weight="duotone" />
              </div>
            </div>
          </div>
          <div className="bg-white border border-slate-200 rounded-lg p-6">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">
                  Eligible to remind
                </div>
                <div className="mt-2 font-heading text-2xl font-bold text-slate-900">
                  {eligible.length}
                </div>
                <div className="mt-0.5 text-[11px] text-slate-500">
                  past 7-day cooldown & has email
                </div>
              </div>
              <div className="w-10 h-10 rounded-md bg-blue-50 text-blue-700 flex items-center justify-center">
                <EnvelopeSimple size={20} weight="duotone" />
              </div>
            </div>
          </div>
        </div>

        {/* Cadence legend */}
        <div className="bg-white border border-slate-200 rounded-lg px-5 py-3 flex flex-wrap items-center gap-3">
          <span className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">
            Email escalates with age:
          </span>
          {CADENCE_TIERS.slice().reverse().map((t) => (
            <span
              key={t.level}
              className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[11px] font-semibold ${t.className}`}
              data-testid={`legend-${t.level}`}
            >
              {t.min_days}d+ · {t.label}
            </span>
          ))}
        </div>

        {/* Filters & bulk action bar */}
        <div className="bg-white border border-slate-200 rounded-lg px-5 py-4 flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <span className="text-xs uppercase tracking-wider text-slate-500">
              Min overdue
            </span>
            <select
              value={minOverdue}
              onChange={(e) => setMinOverdue(Number(e.target.value))}
              className="h-9 px-3 border border-slate-200 rounded-md text-sm bg-white"
              data-testid="min-overdue-select"
            >
              <option value={0}>Any age</option>
              <option value={7}>7+ days</option>
              <option value={14}>14+ days</option>
              <option value={30}>30+ days</option>
              <option value={60}>60+ days</option>
            </select>
          </div>
          <div className="flex-1" />
          <div className="text-sm text-slate-500">
            {selectedIds.length} selected
            {selectedIds.length > 0 &&
              selectedIds.length !== selectedEligibleIds.length && (
                <span className="text-amber-600 ml-2">
                  ({selectedIds.length - selectedEligibleIds.length} on cooldown / no email)
                </span>
              )}
          </div>
          <button
            onClick={sendBulk}
            disabled={sending || selectedEligibleIds.length === 0}
            className="inline-flex items-center gap-2 px-4 h-10 rounded-md bg-rose-600 text-white text-sm font-semibold hover:bg-rose-700 disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="bulk-send-reminders"
          >
            <Bell size={14} weight="bold" />
            {sending
              ? "Sending…"
              : selectedEligibleIds.length > 0
              ? `Send ${selectedEligibleIds.length} reminder${selectedEligibleIds.length === 1 ? "" : "s"}`
              : "Send reminders"}
          </button>
        </div>

        {/* Table */}
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          <table className="w-full text-sm" data-testid="ar-table">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                <th className="px-4 py-3 w-10">
                  <Checkbox
                    checked={allEligibleSelected}
                    onCheckedChange={(v) => toggleAll(!!v)}
                    data-testid="select-all-eligible"
                    aria-label="Select all eligible"
                  />
                </th>
                <th className="px-6 py-3">Invoice</th>
                <th className="px-6 py-3">Customer</th>
                <th className="px-6 py-3">Date</th>
                <th className="px-6 py-3">Age</th>
                <th className="px-6 py-3">Cadence</th>
                <th className="px-6 py-3 text-right">Total</th>
                <th className="px-6 py-3">Reminder status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading && (
                <tr>
                  <td colSpan={8} className="px-6 py-10 text-center text-slate-500">
                    Loading…
                  </td>
                </tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-6 py-12 text-center text-slate-500">
                    🎉 No outstanding invoices matching your filter. All clear!
                  </td>
                </tr>
              )}
              {!loading &&
                filtered.map((inv) => {
                  const age = daysSince(inv.date);
                  const cooldown = recent[inv.id];
                  const hasEmail = (inv.customer?.email || "").trim() !== "";
                  const isEligible = !cooldown && hasEmail;
                  const status = rowStatus[inv.id];
                  return (
                    <tr
                      key={inv.id}
                      className={
                        "transition-colors " +
                        (selected[inv.id] ? "bg-rose-50/40" : "hover:bg-slate-50")
                      }
                      data-testid={`ar-row-${inv.id}`}
                    >
                      <td className="px-4 py-4 align-middle">
                        <Checkbox
                          checked={!!selected[inv.id]}
                          onCheckedChange={(v) =>
                            setSelected((p) => ({ ...p, [inv.id]: !!v }))
                          }
                          disabled={!isEligible}
                          data-testid={`ar-check-${inv.id}`}
                          aria-label={`Select ${inv.invoice_number}`}
                        />
                      </td>
                      <td
                        className="px-6 py-4 cursor-pointer"
                        onClick={() => navigate(`/invoices/${inv.id}`)}
                      >
                        <div className="font-mono text-slate-900 font-semibold">
                          {inv.invoice_number}
                        </div>
                        <div className="text-xs text-slate-500">
                          {inv.items?.length || 0} item(s)
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="font-medium text-slate-900">
                          {inv.customer?.name}
                        </div>
                        <div className="text-xs text-slate-500 truncate max-w-[220px]">
                          {hasEmail ? (
                            inv.customer.email
                          ) : (
                            <span className="text-amber-600 inline-flex items-center gap-1">
                              <WarningCircle size={12} weight="fill" /> no email
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-slate-700">{inv.date}</td>
                      <td className="px-6 py-4">
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded-full border text-xs font-semibold ${ageBadgeClass(age)}`}
                        >
                          {age}d
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[11px] font-semibold ${cadenceFor(age).className}`}
                          title={`Email tone for ${age}-day overdue: ${cadenceFor(age).label}`}
                          data-testid={`cadence-${inv.id}`}
                        >
                          {cadenceFor(age).label}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-slate-900 font-semibold text-right">
                        ₹{fmt(inv.calculation?.total)}
                      </td>
                      <td className="px-6 py-4 text-xs">
                        {status === "sending" && (
                          <span className="text-slate-500">Sending…</span>
                        )}
                        {status === "sent" && (
                          <span className="text-emerald-600 font-medium">
                            ✓ Just sent
                          </span>
                        )}
                        {status && status !== "sending" && status !== "sent" && (
                          <span className="text-red-600" title={status}>
                            ✗ Failed
                          </span>
                        )}
                        {!status && cooldown && (
                          <span className="text-slate-500">
                            Reminded {cooldown.days_ago === 0 ? "today" : `${cooldown.days_ago}d ago`}
                            <span className="text-slate-400">
                              {" "}· {cooldown.cooldown_days_remaining}d cooldown
                            </span>
                          </span>
                        )}
                        {!status && !cooldown && !hasEmail && (
                          <span className="text-amber-600">Missing email</span>
                        )}
                        {!status && !cooldown && hasEmail && (
                          <span className="text-slate-400">Eligible</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
